import React from 'react';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  EmptyState,
  Layout,
} from '@shopify/polaris';
import { useQuery } from 'react-query';
import api from '../services/api';
import Loading from '../components/Loading';

interface ExportRecord {
  id: number;
  orderCount: number;
  format: string;
  tallyVersion: string;
  fileName: string;
  fileSize: number;
  createdAt: string;
  downloadCount: number;
}

const History: React.FC = () => {
  const { data, isLoading, refetch } = useQuery(
    'exportHistory',
    () => api.export.getHistory(),
    { refetchInterval: 30000 }
  );

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const handleDownload = (fileName: string) => {
    window.open(`/api/export/download/${fileName}`, '_blank');
  };

  if (isLoading) {
    return <Loading message="Loading export history..." />;
  }

  const history: ExportRecord[] = data?.data?.data?.history || [];

  const rows = history.map((record) => [
    formatDate(record.createdAt),
    record.orderCount.toString(),
    <Badge tone={record.format === 'xml' ? 'success' : 'info'}>
      {record.format.toUpperCase()}
    </Badge>,
    <Badge tone="info">{record.tallyVersion === 'prime' ? 'TallyPrime' : 'ERP 9'}</Badge>,
    formatFileSize(record.fileSize),
    record.downloadCount.toString(),
    <Button size="slim" onClick={() => handleDownload(record.fileName)}>
      Download
    </Button>,
  ]);

  return (
    <Page
      title="Export History"
      subtitle="View and download your previous exports"
      primaryAction={{
        content: 'Refresh',
        onAction: () => refetch(),
      }}
    >
      <Layout>
        <Layout.Section>
          {history.length === 0 ? (
            <Card>
              <EmptyState
                heading="No exports yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                action={{
                  content: 'Export Orders',
                  url: '/export',
                }}
              >
                <p>You haven't exported any orders yet. Start by exporting your first batch of orders.</p>
              </EmptyState>
            </Card>
          ) : (
            <Card>
              <DataTable
                columnContentTypes={['text', 'numeric', 'text', 'text', 'text', 'numeric', 'text']}
                headings={['Date', 'Orders', 'Format', 'Version', 'Size', 'Downloads', 'Action']}
                rows={rows}
                footerContent={`Showing ${history.length} exports`}
              />
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default History;
